import { useEffect, useState } from 'react';
import {
  NOT_ASSESSED,
  seatLabel,
  type EventAttendee,
  type EventAttendeeCandidate,
  type EventAttendeesResponse,
  type EventFrameDef,
} from '@ni/shared';
import { useEventAttendee, useEventAttendees } from '../../api/event-hooks';
import { ErrorLine } from '../../components/ui/error-line';
import { EmptyLine } from '../../components/ui/empty-line';
import { Loading } from '../../components/ui/loading';
import { WorkingButton } from '../../components/ui/working-button';
import {
  ATTENDEE_BINDING_LABELS,
  EVENT_ANALYSIS_NOBODY,
  EVENT_ANALYSIS_TOTAL,
  EVENT_ATTENTION_CHECK_WHO,
  EVENT_ATTENTION_NEEDS_REVIEW,
  EVENT_SEAT_WORD,
  EVENT_SHOW_EVERY_GROUP,
  EVENT_TIER_HINTS,
  LOADING_MORE,
  LOAD_MORE,
  eventAnalysisReviewLine,
  eventAnalysisReviewMore,
  eventAttentionMore,
  eventFactorConclusion,
  eventFilterLine,
  eventFrameTierLabel,
  eventPassagesDisclosure,
  eventRatedDisclosure,
} from '../../lib/copy';
import { attendeeDetails, AttendeesTable } from './attendees';
import { Legend, Ring, type ChartSegment } from './analysis-chart';
import { SeatingPlan, seatedOf } from './seating';

/** How a tier reads on screen: its place in the frame, never its words. */
export function tierTone(
  frame: EventFrameDef,
  tier: string,
): 'concern' | 'unknown' | 'quiet' | 'clear' | 'none' {
  if (tier === NOT_ASSESSED) return 'none';
  if (tier === frame.clear) return 'clear';
  if (tier === frame.unresolved) return 'unknown';
  if (tier === frame.unplaced) return 'quiet';
  return 'concern';
}

/**
 * **A frame's palette**: a screening frame (one that keeps "No flags" apart
 * from clear) is a risk analysis; any other is an opportunity analysis.
 */
export function framePalette(frame: EventFrameDef): 'risk' | 'opportunity' {
  return frame.unplaced !== null ? 'risk' : 'opportunity';
}

/** A tier's chart color class (analysis.css), or `hatch` for those not assessed. */
export function tierSwatch(frame: EventFrameDef, tier: string | null): string {
  const tone = tierTone(frame, tier ?? NOT_ASSESSED);
  return tone === 'none' ? 'hatch' : `c-${tone}`;
}

/** One entry per tier, most concerning first, then those the frame has not rated. */
export function tilesOf(
  frame: EventFrameDef,
  counts: { counts: Record<string, number>; not_assessed: number } | undefined,
): { tier: string; label: string; count: number }[] {
  return [
    ...frame.tiers.map((tier) => ({
      tier: tier.key,
      label: tier.label,
      count: counts?.counts[tier.key] ?? 0,
    })),
    {
      tier: NOT_ASSESSED,
      label: eventFrameTierLabel(frame, null),
      count: counts?.not_assessed ?? 0,
    },
  ];
}

export function factorSegments(
  frame: EventFrameDef,
  tiles: ReturnType<typeof tilesOf>,
): ChartSegment[] {
  return tiles.map((tile) => ({
    key: tile.tier,
    label: tile.label,
    count: tile.count,
    swatch: tierSwatch(frame, tile.tier),
    hint: EVENT_TIER_HINTS[tierTone(frame, tile.tier)],
  }));
}

/** The tiers whose attendees open on the tab: the raised group, then those to check. */
export function attentionTiers(frame: EventFrameDef): string[] {
  return frame.tiers
    .map((tier) => tier.key)
    .filter((tier) => ['concern', 'unknown'].includes(tierTone(frame, tier)));
}

/** How many of a group open on the tab before the rest wait in the disclosure. */
const ATTENTION_SHOWN = 6;
/** How many raised names the conclusion says before "and others". */
const CONCLUSION_NAMES = 3;

export interface Pick {
  open: boolean;
  tier: string | null;
}

/**
 * **One factor's summary**: the ring of how the attendees fall across its
 * groups, a one-line conclusion naming the raised and where the list seats
 * them, a legend that filters the groups below, and the attention case open
 * on the tab, each entry with its seat. A risk factor adds the seating.
 */
export function FactorSummary({
  entityId,
  frame,
  counts,
  seated,
  everyone,
  pick,
  onPick,
  onOpen,
}: {
  entityId: string;
  frame: EventFrameDef;
  counts: { counts: Record<string, number>; not_assessed: number } | undefined;
  seated: number;
  /** The loaded attendee list, for the seating. */
  everyone: readonly EventAttendee[];
  pick: Pick;
  onPick: (next: Pick) => void;
  onOpen: (nodeId: string) => void;
}) {
  const tiles = tilesOf(frame, counts);
  const segments = factorSegments(frame, tiles);
  const total = tiles.reduce((sum, tile) => sum + tile.count, 0);
  const [raisedTier] = attentionTiers(frame).filter((tier) => tierTone(frame, tier) === 'concern');
  const raised = useEventAttendees(entityId, { frame: frame.key, tier: raisedTier ?? frame.clear });
  const raisedRows = raised.data?.pages.flatMap((page) => page.attendees) ?? [];
  const count = (tier: string) => tiles.find((tile) => tile.tier === tier)?.count ?? 0;
  const label = (tier: string | null) => eventFrameTierLabel(frame, tier);

  return (
    <div className="an-factor-block" data-palette={framePalette(frame)}>
      <h3 className="an-factor">{frame.label}</h3>
      <figure className="an-fig">
        <Ring segments={segments} totalLabel={EVENT_ANALYSIS_TOTAL} />
        <figcaption className="an-cap">
          <p className="an-says">
            {total > 0
              ? eventFactorConclusion(
                  frame.key,
                  total,
                  raisedRows.slice(0, CONCLUSION_NAMES).map((row) => ({
                    name: row.name,
                    seat: seatLabel(row.identifiers),
                  })),
                  raisedTier ? count(raisedTier) : 0,
                  count(frame.unresolved),
                )
              : null}
          </p>
          <Legend
            segments={segments}
            picked={pick.open ? pick.tier : null}
            onPick={(tier) => onPick({ open: true, tier })}
          />
        </figcaption>
      </figure>

      <div className="an-watch">
        {attentionTiers(frame).map((tier) => (
          <AttentionGroup
            key={tier}
            entityId={entityId}
            frame={frame}
            tier={tier}
            count={count(tier)}
            onMore={() => onPick({ open: true, tier })}
            onOpen={onOpen}
          />
        ))}
      </div>

      {framePalette(frame) === 'risk' && seated > 0 ? (
        <SeatingPlan
          attendees={seatedOf(everyone, frame.key)}
          seated={seated}
          raised={(who) => who.tier !== null && who.tier === raisedTier}
          tierLabel={label}
          tierSwatch={(tier) => tierSwatch(frame, tier)}
        />
      ) : null}
    </div>
  );
}

/** How many names the review line says before "and N more". */
const REVIEW_NAMES = 8;

/**
 * **The attendees the scan could not tell apart**, named at the top of the
 * analysis, each opening the readings where the office says which is them.
 * They are also in their groups below, but a reading to settle should not have
 * to be found there or on the Attendees tab.
 */
export function ReviewLine({
  entityId,
  count,
  reportHeld,
  onOpen,
}: {
  entityId: string;
  count: number;
  /** The scan's report waits on these attendees. */
  reportHeld: boolean;
  onOpen: (nodeId: string) => void;
}) {
  const list = useEventAttendees(entityId, { review: 'true' });
  const rows = (list.data?.pages.flatMap((page) => page.attendees) ?? []).slice(0, REVIEW_NAMES);
  if (rows.length === 0) return null;
  return (
    <p className="an-review">
      {eventAnalysisReviewLine(count, reportHeld)}{' '}
      {rows.map((row, index) => (
        <span key={row.id}>
          {index > 0 ? ', ' : null}
          <button type="button" className="linkish flush" onClick={() => onOpen(row.id)}>
            {row.name}
          </button>
        </span>
      ))}
      {count > rows.length ? ` ${eventAnalysisReviewMore(count - rows.length)}` : null}
    </p>
  );
}

/** One attention group: its head with the count, then its first entries in full. */
function AttentionGroup({
  entityId,
  frame,
  tier,
  count,
  onMore,
  onOpen,
}: {
  entityId: string;
  frame: EventFrameDef;
  tier: string;
  count: number;
  onMore: () => void;
  onOpen: (nodeId: string) => void;
}) {
  const list = useEventAttendees(entityId, { frame: frame.key, tier });
  const rows = list.data?.pages.flatMap((page) => page.attendees) ?? [];
  return (
    <div className="an-group">
      <div className="sub">
        <span className={`an-sw ${tierSwatch(frame, tier)}`} />
        <h4>{eventFrameTierLabel(frame, tier)}</h4>
        <span className="c num">{count}</span>
      </div>
      {count === 0 ? (
        <p className="an-nobody">{EVENT_ANALYSIS_NOBODY}</p>
      ) : list.isPending ? (
        <EmptyLine>
          <Loading />
        </EmptyLine>
      ) : list.isError ? (
        <ErrorLine error={list.error} load />
      ) : (
        <>
          <ul className="atts">
            {rows.slice(0, ATTENTION_SHOWN).map((row) => (
              <AttentionEntry
                key={row.id}
                entityId={entityId}
                frame={frame}
                attendee={row}
                onOpen={onOpen}
              />
            ))}
          </ul>
          {count > ATTENTION_SHOWN ? (
            <button type="button" className="linkish flush an-more" onClick={onMore}>
              {eventAttentionMore(count - ATTENTION_SHOWN)}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

/** The readings that put the attendee in their group on this frame. */
export function readingsBehind(
  candidates: readonly EventAttendeeCandidate[],
  frame: string,
  tier: string | null,
): { reading: EventAttendeeCandidate; reason: string; findingIds: string[] }[] {
  return candidates.flatMap((reading) => {
    const assessment = reading.assessments.find(
      (one) => one.frame === frame && (tier === null || one.tier === tier),
    );
    return assessment
      ? [{ reading, reason: assessment.reason, findingIds: assessment.finding_ids }]
      : [];
  });
}

/**
 * **An attendee in the attention case**, in full: the name (which opens their
 * readings and the office's answers), what the list gives, the seat, then each
 * reading that put them in this group with its reason and, on demand, the
 * passages it rests on.
 */
function AttentionEntry({
  entityId,
  frame,
  attendee,
  onOpen,
}: {
  entityId: string;
  frame: EventFrameDef;
  attendee: EventAttendee;
  onOpen: (nodeId: string) => void;
}) {
  const detail = useEventAttendee(entityId, attendee.id);
  const tier = attendee.frames.find((one) => one.frame === frame.key)?.tier ?? null;
  const seat = seatLabel(attendee.identifiers);
  const details = attendeeDetails(attendee.identifiers);
  const behind = readingsBehind(detail.data?.candidates ?? [], frame.key, tier).slice(0, 2);
  return (
    <li className="att">
      <div className="att-top">
        <button type="button" className="person att-name" onClick={() => onOpen(attendee.id)}>
          {attendee.name}
        </button>
        {details ? <span className="att-meta">{details}</span> : null}
        {seat ? (
          <span className="att-seat">
            <span className="k">{EVENT_SEAT_WORD}</span>
            {seat}
          </span>
        ) : null}
      </div>
      {attendee.needs_review ? (
        <p className="att-sev">
          <b>{eventFrameTierLabel(frame, tier)}</b>
          <span className="sep">·</span>
          {EVENT_ATTENTION_NEEDS_REVIEW}
          <span className="sep">·</span>
          <button type="button" className="linkish flush" onClick={() => onOpen(attendee.id)}>
            {EVENT_ATTENTION_CHECK_WHO}
          </button>
        </p>
      ) : null}
      {behind.map(({ reading, reason, findingIds }) => {
        const passages = reading.findings.filter((finding) => findingIds.includes(finding.id));
        return (
          <div className="att-read" key={reading.id}>
            <p className="att-label">
              {reading.label}
              <span className="bind">{ATTENDEE_BINDING_LABELS[reading.binding]}</span>
            </p>
            <p className="att-why">{reason}</p>
            {passages.length > 0 ? (
              <details className="att-more">
                <summary>{eventPassagesDisclosure(passages.length)}</summary>
                <ol>
                  {passages.map((finding) => (
                    <li key={finding.id}>
                      <q>{finding.quote}</q>
                      {finding.url ? (
                        <a className="src-note" href={finding.url} target="_blank" rel="noreferrer">
                          {hostOf(finding.url)}
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ol>
              </details>
            ) : null}
          </div>
        );
      })}
    </li>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * **Everyone, by group, behind one disclosure.** The legend opens it on one
 * group; "Show every group" lets go of the filter.
 */
export function FactorGroups({
  entityId,
  frame,
  counts,
  seated,
  pick,
  onPick,
  onOpen,
  titled,
}: {
  entityId: string;
  frame: EventFrameDef;
  counts: { counts: Record<string, number>; not_assessed: number } | undefined;
  seated: boolean;
  pick: Pick;
  onPick: (next: Pick) => void;
  onOpen: (nodeId: string) => void;
  /** More than one factor: the disclosure names its own. */
  titled: boolean;
}) {
  const tiles = tilesOf(frame, counts);
  const total = tiles.reduce((sum, tile) => sum + tile.count, 0);
  const picked = pick.tier ? tiles.find((tile) => tile.tier === pick.tier) : undefined;
  return (
    <details
      className="an-behind"
      data-palette={framePalette(frame)}
      open={pick.open}
      onToggle={(event) => {
        const open = (event.currentTarget as HTMLDetailsElement).open;
        if (open !== pick.open) onPick({ open, tier: open ? pick.tier : null });
      }}
    >
      <summary>
        {eventRatedDisclosure(total)}
        {titled ? <span className="quiet"> · {frame.label}</span> : null}
      </summary>
      {pick.open ? (
        <>
          {picked ? (
            <p className="an-filter-line">
              <span>{eventFilterLine(picked.label, picked.count, total)}</span>
              <button
                type="button"
                className="linkish flush"
                onClick={() => onPick({ open: true, tier: null })}
              >
                {EVENT_SHOW_EVERY_GROUP}
              </button>
            </p>
          ) : null}
          {tiles
            .filter((tile) => !picked || tile.tier === picked.tier)
            .map((tile) => (
              <TierGroup
                key={tile.tier}
                entityId={entityId}
                frame={frame}
                tier={tile.tier}
                label={tile.label}
                count={tile.count}
                seated={seated}
                onOpen={onOpen}
              />
            ))}
        </>
      ) : null}
    </details>
  );
}

function TierGroup({
  entityId,
  frame,
  tier,
  label,
  count,
  seated,
  onOpen,
}: {
  entityId: string;
  frame: EventFrameDef;
  tier: string;
  label: string;
  count: number;
  seated: boolean;
  onOpen: (nodeId: string) => void;
}) {
  return (
    <div className="tier-group">
      <div className="sub">
        <span className={`an-sw ${tierSwatch(frame, tier)}`} />
        <h4>{label}</h4>
        <span className="c num">{count}</span>
      </div>
      {count === 0 ? (
        <p className="an-nobody">{EVENT_ANALYSIS_NOBODY}</p>
      ) : (
        <TierRows entityId={entityId} frame={frame} tier={tier} seated={seated} onOpen={onOpen} />
      )}
    </div>
  );
}

function TierRows({
  entityId,
  frame,
  tier,
  seated,
  onOpen,
}: {
  entityId: string;
  frame: EventFrameDef;
  tier: string;
  seated: boolean;
  onOpen: (nodeId: string) => void;
}) {
  const list = useEventAttendees(entityId, { frame: frame.key, tier });
  if (list.isPending)
    return (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  if (list.isError) return <ErrorLine error={list.error} load />;
  const rows = list.data.pages.flatMap((page) => page.attendees);
  return (
    <>
      <AttendeesTable rows={rows} seated={seated} onOpen={onOpen} />
      {list.hasNextPage ? (
        <div className="list-foot">
          <WorkingButton
            label={LOAD_MORE}
            workingLabel={LOADING_MORE}
            working={list.isFetchingNextPage}
            variant="act"
            tiny
            onClick={() => void list.fetchNextPage()}
          />
        </div>
      ) : null}
    </>
  );
}

/** How many pages of the attendee list the tab reads for its charts and seating. */
const WHOLE_LIST_PAGES = 10;

/**
 * **The attendee list as far as the tab reads it**: every page, up to a bound,
 * for the charts and the seating that group the whole list in the client.
 * `complete` is false while more remains past what is loaded.
 */
export function useWholeList(
  entityId: string,
  needed: boolean | ((first: EventAttendeesResponse) => boolean),
) {
  const list = useEventAttendees(entityId, {});
  const pages = list.data?.pages ?? [];
  const first = pages[0];
  const wanted = first !== undefined && (typeof needed === 'boolean' ? needed : needed(first));
  /* Paged on only where the whole list is drawn: the shared query polls every
     page it holds. */
  const more = wanted && list.hasNextPage && pages.length < WHOLE_LIST_PAGES;
  const { isFetchingNextPage, fetchNextPage } = list;
  useEffect(() => {
    if (more && !isFetchingNextPage) void fetchNextPage();
  }, [more, isFetchingNextPage, fetchNextPage]);
  return {
    list,
    rows: pages.flatMap((page) => page.attendees),
    complete: !list.hasNextPage,
  };
}

/** The picks of each factor of an analysis, kept on the panel. */
export function usePicks() {
  const [picks, setPicks] = useState<Record<string, Pick>>({});
  return {
    pickOf: (frame: string): Pick => picks[frame] ?? { open: false, tier: null },
    setPick: (frame: string, next: Pick) => setPicks((all) => ({ ...all, [frame]: next })),
  };
}
